import React, {
  useEffect,
  useContext,
  useCallback,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from "./TasksPage.module.css";
import { TaskHeader } from "./components/TaskHeader/TaskHeader";
import { TaskItem } from "./components/TaskItem/TaskItem";
import useApplicationSettings from "../../Store/ApplicationSettings";
import { TasksContext, TasksProvider } from "./provider/TasksProvider";
import { Task, TaskType, TraffyTask } from "./types";
import { TaskModal } from "../../Components/TaskModal/TaskModal";
import { useModalStore } from "../../Store/ModalStore";
import { Loader } from "../../Components/Loader/Loader";
import RefreshIcon from "/RefreshIcon.svg";
import { useMainButton } from "../../Components/MainButton/MainButton";
import { BecomePartners } from "../../Components/BecomePartners/BecomePartners";
// Убираем импорт refreshSponsorsTasks, так как теперь используем функцию из стора
import { useTasksStore } from "../../Store/TasksStore";
import axios from "axios";
import { settings } from "../../Services/Requests";
import { TraffyTasksList } from "./components/TraffyTasksList/TraffyTasksList";

const taskTypes = {
  sponsors: {
    title: "Спонсорские",
    description: "Выполняй задания и получай награду.",
    image: "/sponsor.webp",
    centerIcon: "/sponsors_v2.png",
  },
  partners: {
    title: "Партнерские",
    description: "Выполняй задания и получай награду.",
    image: "/partners.webp",
    centerIcon: "/partners_v2.png",
  },
  "quest-daily": {
    title: "Игровые",
    description: "Выполняй задания и получай награду.",
    image: "/daily.webp",
    centerIcon: "/daily_v2.png",
  },
} as const;

const TasksPageBase: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const location = useLocation();
  const groupId = location.state?.groupId;
  const { tasks, fetchTasks, loading } = useContext(TasksContext);
  const navigate = useNavigate();
  const tg = window.Telegram.WebApp;
  const setShowMenubar = useApplicationSettings(
    (state: any) => state.setShowMenubar
  );
  const setCurrentTask = useModalStore((state) => state.setCurrentTask);
  const modalOpenRef = useRef(false);
  const [traffyTasks, setTraffyTasks] = useState<TraffyTask[]>([]);

  // Используем состояние таймера из стора
  const isRefreshing = useTasksStore((state) => state.isRefreshing);
  const timeLeft = useTasksStore((state) => state.timeLeft);
  const startRefreshTimer = useTasksStore((state) => state.startRefreshTimer);

  // Функция форматирования времени
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const [RefreshButton, refreshButtonController] = useMainButton({
    text: isRefreshing
      ? `Обновить список (${formatTime(timeLeft)})`
      : "Обновить список",
    icon: isRefreshing ? undefined : RefreshIcon,
    color: "rgba(255, 255, 255, 0.14)",
    isActive: !isRefreshing,
  });

  console.log(location, "SFSFSFF");
  useEffect(() => {
    const setup = () => {
      setShowMenubar(false);
      tg.BackButton.show();
      tg.BackButton.onClick(() => navigate(-1));
    };

    setup();
    fetchTasks(type as TaskType, groupId);

    return () => {
      tg.BackButton.hide();
      setShowMenubar(true);
    };
  }, [type, navigate, groupId, fetchTasks]);

  const handleBecomePartner = useCallback(() => {
    tg.openTelegramLink("https://t.me/dreamcoin_ads");
  }, [tg]);

  const handleTaskAction = useCallback(
    (taskId: number | string) => {
      if (modalOpenRef.current) return;

      console.log("🎯 Opening modal for taskId:", taskId);
      modalOpenRef.current = true;

      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        setCurrentTask(task);
      }
    },
    [tasks, setCurrentTask]
  );

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;

    startRefreshTimer(); // Запускаем таймер из стора

    try {
      if (type === "sponsors") {
        const refreshSponsorsTasks =
          useTasksStore.getState().refreshSponsorsTasks;
        refreshSponsorsTasks();
      } else {
        await fetchTasks(type as TaskType, groupId);
      }
    } catch (error) {
      console.error("Error refreshing tasks:", error);
      // Останавливаем таймер при ошибке
      useTasksStore.getState().stopRefreshTimer();
    }
  }, [fetchTasks, type, groupId, isRefreshing, startRefreshTimer]);

  // Обновляем текст и иконку кнопки при изменении timeLeft или isRefreshing
  useEffect(() => {
    refreshButtonController.setText(
      isRefreshing
        ? `Обновить список (${formatTime(timeLeft)})`
        : "Обновить список"
    );

    // Иконка показывается только когда нет таймера
    if (isRefreshing) {
      refreshButtonController.setIcon(undefined);
    } else {
      refreshButtonController.setIcon(RefreshIcon);
    }
  }, [timeLeft, isRefreshing]);

  useEffect(() => {
    refreshButtonController.onClick(handleRefresh);
    return () => refreshButtonController.offClick();
  }, [handleRefresh]);

  useEffect(() => {
    if (isRefreshing) {
      refreshButtonController.disable();
    } else {
      refreshButtonController.enable();
    }
  }, [isRefreshing]);

  console.log("🔍 TasksPage - текущий тип:", type);
  console.log("🔍 TasksPage - количество заданий:", tasks.length);
  console.log("🔍 TasksPage - задания:", tasks);

  const sortStatus = [
    "DONE",
    "NOT_STARTED",
    "IN_PROGRESS",
    "WAITING",
    "CLAIMED",
  ];
  const tasksWithGroup = tasks
    .map((task) => ({
      ...task,
      group: "individual" as const,
    }))
    .sort((a, b) => {
      return sortStatus.indexOf(a.status) - sortStatus.indexOf(b.status);
    });

  if (!type || !taskTypes[type as keyof typeof taskTypes]) {
    return null;
  }

  return (
    <div className={styles.tasksPage}>
      <TaskHeader
        {...taskTypes[type as keyof typeof taskTypes]}
        isQuest={type.startsWith("quest-")}
        groupType={type}
      />
      <div className={styles.tasksList}>
        {loading ? (
          <Loader size="medium" text="Загружаем задания..." />
        ) : tasks.length > 0 ? (
          <>
            {tasksWithGroup.map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                onTaskAction={handleTaskAction}
              />
            ))}
            {location.pathname === "/tasks/sponsors" && (
              <TraffyTasksList setTasks={setTraffyTasks} />
            )}

            {(type === "partners" || type === "sponsors") && (
              <BecomePartners
                type={type as "partners" | "sponsors"}
                handleBecomePartner={handleBecomePartner}
                style={{ marginTop: "16px" }}
              />
            )}
          </>
        ) : (
          <>
            {location.pathname === "/tasks/sponsors" && (
              <TraffyTasksList setTasks={setTraffyTasks} />
            )}
            <div
              className={
                traffyTasks.length
                  ? styles.tasksListEmpty
                  : styles.tasksListEmptyDisplay
              }
            >
              Сейчас заданий нет, но они могут появиться в любой момент.
              Заглядывай чаще!
            </div>
            {(type === "partners" || type === "sponsors") && (
              <BecomePartners
                type={type as "partners" | "sponsors"}
                handleBecomePartner={handleBecomePartner}
                style={{ marginTop: "16px" }}
              />
            )}
          </>
        )}
      </div>
      <TaskModal />
      <div className={styles.refreshButtonWrapper}>
        <RefreshButton />
      </div>
    </div>
  );
};

const TasksPageContent = React.memo(TasksPageBase);

const TasksPage: React.FC = () => {
  return (
    <TasksProvider>
      <TasksPageContent />
    </TasksProvider>
  );
};

export default TasksPage;
